import { create } from "zustand"
import { persist } from "zustand/middleware"

import {
  functionalUpdate,
  type ColumnOrderState,
  type ColumnSizingState,
  type ColumnVisibilityState,
  type SortingState,
  type Updater,
} from "@atlant/ui"

interface TableSettingsState {
  columnOrder: ColumnOrderState
  columnVisibility: ColumnVisibilityState
  columnSizing: ColumnSizingState
  sorting: SortingState
}

interface TableSettings extends TableSettingsState {
  setColumnOrder: (updater: Updater<ColumnOrderState>) => void
  setColumnVisibility: (updater: Updater<ColumnVisibilityState>) => void
  setColumnSizing: (updater: Updater<ColumnSizingState>) => void
  setSorting: (updater: Updater<SortingState>) => void
}

// Per-table column order / visibility / widths / sorting, persisted in localStorage.
// Spread `tableSettingsOptions(settings)` into useTable; defaults double as the
// table's initialState so "Reset to defaults" returns to them.
export const createTableSettingsStore = (
  name: string,
  defaults: Partial<TableSettingsState> = {},
) => {
  const initial: TableSettingsState = {
    columnOrder: [],
    columnVisibility: {},
    columnSizing: {},
    sorting: [],
    ...defaults,
  }

  return create<TableSettings>()(
    persist(
      (set, get) => ({
        ...initial,
        setColumnOrder: (u) =>
          set({ columnOrder: functionalUpdate(u, get().columnOrder) }),
        setColumnVisibility: (u) =>
          set({ columnVisibility: functionalUpdate(u, get().columnVisibility) }),
        setColumnSizing: (u) =>
          set({ columnSizing: functionalUpdate(u, get().columnSizing) }),
        setSorting: (u) => set({ sorting: functionalUpdate(u, get().sorting) }),
      }),
      { name },
    ),
  )
}

export const tableSettingsOptions = ({
  columnOrder,
  columnVisibility,
  columnSizing,
  sorting,
  setColumnOrder,
  setColumnVisibility,
  setColumnSizing,
  setSorting,
}: TableSettings) => ({
  state: { columnOrder, columnVisibility, columnSizing, sorting },
  onColumnOrderChange: setColumnOrder,
  onColumnVisibilityChange: setColumnVisibility,
  onColumnSizingChange: setColumnSizing,
  onSortingChange: setSorting,
})
